/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { format } from '@msdyn365-commerce-modules/utilities';
import { getUrlSync, IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { Cart } from '@msdyn365-commerce/retail-proxy';
import { observer } from 'mobx-react';
import * as React from 'react';

export interface ICartIconData {
    cart: ICartState;
}

export interface ICartIconComponentProps extends IComponentProps<ICartIconData> {
    className?: string;
    cartLabel: string;
}

export interface ICartIconComponent extends IComponent<ICartIconComponentProps> {
}

const CartIconComponentActions = {
};

interface ICartIconState {
    countChanged: boolean;
}

interface ICartLineData{
    quantity?: number;
    price?: number;
    item?: string;
  }

/**
 *
 * CartIcon component
 * @extends {React.FC<ICartIconComponentProps>}
 */
@observer
class CartIcon extends React.Component<ICartIconComponentProps, ICartIconState> {
    private cartItemCount: string | undefined;

    constructor(props: ICartIconComponentProps) {
        super(props);

        this.state = {
            countChanged: false
        };
    }
    public render(): JSX.Element {
        const { cartLabel, data: { cart } } = this.props;

        if (!cart) {
            return (
                <a className={`msc-cart-icon msc-btn`} >
                    <div className='msc-cart-icon__count' aria-hidden={true}>
                    </div>
                </a>
            );
        }

        const cartLink = getUrlSync('cart', this.props.context.actionContext);

        const cartItem = `${this._getCartLineQuantities(cart.cart)}`;

        const label = format(cartLabel, cartItem) || `Shopping bag, ${cartItem} items`;

        if (!this.state.countChanged && this.cartItemCount && this.cartItemCount < cartItem) {
            this.setState({
                countChanged: true
            });
            // tslint:disable-next-line: no-floating-promises
            this._resetClass();
        }
        this.cartItemCount = cartItem;
        const hiddenCartLines = this._createHiddenCartLines(cart.cart);
        return (
            <a className={`msc-cart-icon msc-btn ${this.state.countChanged ? 'bounce' : ''}`} href={cartLink} aria-label={label} >
                <div className='msc-cart-icon__count' aria-hidden={true}>
                    ({cartItem})
                </div>
                {hiddenCartLines}
            </a>
        );
    }

    private _timeout(ms: number): Promise<unknown> {
        // tslint:disable-next-line: no-string-based-set-timeout
        return new Promise(res => setTimeout(res, ms));
    }

    private async _resetClass(): Promise<void> {
        await this._timeout(1500);
        this.setState({
            countChanged: false
        });
        return;
    }

    private _getCartLineQuantities = (cart: Cart | undefined): string => {
        if(!cart) {
            return '0';
        }

        let quantity = 0;

        if(cart.CartLines && cart.CartLines.length > 0) {
            cart.CartLines.forEach(cartLine => {
                quantity += cartLine.Quantity || 1;
            });
        }

        return quantity.toString();
    }

    private _createHiddenCartLines = (cart: Cart | undefined): any => {
        if (!cart || !cart.CartLines?.length) {
            return <div data-cart='[]'></div>;
        }

        let cartLines: ICartLineData[] = [];

        cart.CartLines?.forEach(line => {
            cartLines.push({
                quantity: line.Quantity,
                price: line.Price,
                item: line.ItemId
            });
        });

        return <div id="emarsys-cart-lines" style={{display: 'none'}} data-cart={JSON.stringify(cartLines)}></div>;
    }
}

// @ts-ignore
export const CartIconComponent: React.FunctionComponent<ICartIconComponentProps> = msdyn365Commerce.createComponent<ICartIconComponent>(
    'CartIcon',
    { component: CartIcon, ...CartIconComponentActions }
);